import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ParseKeys } from 'i18next';

import CTA from 'Src/components/buttons/CTA';
import { IconCTA } from 'Src/components/buttons/IconCTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { UsedCurrentCheckItemButtons } from 'Src/feature/check-item/useCurrentCheckItemButtons';
import CheckItemSplitButton from 'Src/feature/check-split/CheckItemSplitButton';
import ChecksSplitBySeat, { ChecksSplitBySeatType } from 'Src/feature/check-split/ChecksSplitBySeat';
import { useCheckSplit } from 'Src/feature/check-split/useCheckSplit';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { useToastWhen } from 'Src/feature/toasts/useToastWhen';

import styles from './CheckPageSplit.module.scss';

const sectionId = 'CheckPageSplit';

interface CheckPageSplitProps {
  checkItemButtons: UsedCurrentCheckItemButtons;
}

/**
 * View Component for splitting check(s) & check item(s)
 * - split check-item(s) onto new check(s)
 * - split by seat position
 * - TODO: split by number
 * - split manually
 */
const CheckPageSplit: FC<CheckPageSplitProps> = ({ checkItemButtons }) => {
  const { t } = useTranslation();

  const currentCheckSelectedCheckItemIds = checkItemButtons.selectedCheckItems.map(({ id }) => id);

  const { store, initialize } = useCheckSplit({ currentCheckSelectedCheckItemIds });

  // conditional toast to show when a user is unable to split
  useToastWhen({
    id: `${sectionId}.cantSplit`,
    type: 'alert',
    props: {
      message: t('checkSplit.info.cantSplitAlreadySplit'),
      title: t('labels.warning'),
      severity: 'warning',
    },
  }, () => store.hasSomeAlreadySplitCheckItemSelections(store.checks));

  return (
    <Page
      data-testid={sectionId}
      className={styles.root}
      main={(
        <PageMain
          className={styles['page-main']}
          header={(
            <PageMainHeader
              start={(
                <Copy variant="h6" value={t('checkSplit.label')} />
              )}
              end={(
                <Flex horizontal alignItems="center">
                  <CTA variant="outlined" onClick={store.onBack} label={t('labels.cancel')} />
                  {
                    store.can.cancel && (
                      <CTA onClick={initialize} label={t('checkSplit.labels.reset')} className={styles.resetBtn} />
                    )
                  }
                  {
                    store.seats.can.assignSeatPosition && (
                      <ChecksSplitBySeat
                        seatNames={store.seats.selectedSeatNames}
                        type={ChecksSplitBySeatType.Assign}
                        onDone={store.seats.assignSeatPositions}
                      />
                    )
                  }
                  {
                    store.splitOrMove.is.active && (
                      <CTA
                        color="primary"
                        onClick={() => store.splitOrMove.onButtonClick()}
                        label={(() => {
                          switch (store.splitOrMove.type) {
                            case 'split-and-move': return t('checkSplit.labels.splitItemAndMove');
                            case 'split': return t('checkSplit.labels.split');
                            case 'move': return t('checkSplit.labels.move');
                            default: return '';
                          }
                        })()}
                      />
                    )
                  }
                  {
                    store.can.save && (
                      <>
                        <CTA
                          color="primary"
                          label={t('checkSplit.labels.doneAndPrint')}
                          onClick={() => {
                            void store.onDone(true);
                          }}
                        />
                        <CTA
                          color="primary"
                          label={t('labels.done')}
                          onClick={() => {
                            void store.onDone();
                          }}
                        />
                      </>
                    )
                  }
                </Flex>
              )}
            />
          )}
          main={(
            <>
              <div style={store.grid.style}>
                {
                  store.checks.map((check, chkIdx) => {
                    const canMoveHere = store.can.moveHere(check);
                    const canSplitHere = store.can.splitHere(check);
                    const hasFooter = canMoveHere || canSplitHere;
                    const hasAddBtn = chkIdx === store.checks.length - 1 && check.items.length === 0;
                    const subHeaderTransKey: ParseKeys<'translation'> = `checkSplit.labels.checkSubHeader${check.tableName !== '' ? '-hasTable' : ''}${check.items.length > 0 ? '-hasItems' : ''}`;
                    return (
                      <div
                        key={check.id}
                        className={classNames(styles['card'], {
                          [styles['card-selected']]: check.isSelected,
                          [styles['placeholder-chk']]: !check.isSelected && hasAddBtn,
                        })}
                      >
                        <Flex className={styles['card-title']} fullWidth gap="none">
                          <Copy variant="h6" value={check.label} className={styles['check_label']} />
                          <Copy value={t(subHeaderTransKey, {
                            tableName: check.tableName,
                            checkNumber: chkIdx + 1,
                            // we only want to count items that have not been Removed, Moved, or Voided
                            itemCount: check.items.filter(({ status }) => status !== 'Removed' && status !== 'Moved' && status !== 'Voided').length,
                          })} />
                        </Flex>
                        <div className={classNames(styles['items'], {
                          [styles['items-with-actions']]: canMoveHere || canSplitHere,
                          [styles['has-add-btn']]: hasAddBtn,
                        })}>
                          {hasAddBtn && <IconCTA variant="outlined" icon="add" size="lg" onClick={() => void store.onAddNewCheck()} />}
                          {
                            check.items.map((item) => (
                              <CheckItemSplitButton
                                key={item.uid}
                                checkItem={item}
                                onClick={store.select.checkItem}
                                someSelections={store.has.selections}
                              />
                            ))
                          }
                        </div>
                        {hasFooter && (
                          <Flex horizontal fillItems className={styles['card-footer']}>
                            {
                              canSplitHere && (
                                check.isSelected
                                  ? <CTA size="lg" fullWidth pill label={t('checkSplit.labels.deselect')} onClick={() => store.select.check(check)} color="primary" />
                                  : <CTA size="lg" fullWidth pill label={t('checkSplit.labels.splitHere')} onClick={() => store.select.check(check)} color="secondary" />
                              )
                            }
                            {
                              canMoveHere && (
                                <CTA size="lg" color="primary" fullWidth pill onClick={() => store.onMoveHereClick(check)} label={t('checkSplit.labels.moveHere')} />
                              )
                            }
                          </Flex>
                        )}
                      </div>
                    );
                  })
                }
                <Flex gap={3} alignItems="center" justifyContent="center">
                  {store.can.splitBySeat && <CTA color="primary" label={t('checkSplit.labels.splitBySeatPosition')} onClick={store.onSplitBySeat} />}
                </Flex>
              </div>
              {/* keeping the ChecksSplitBySeat commented in case we want it to come back */}
              {/* {
                  store.seats.can.createSeatChecks && (
                    <ChecksSplitBySeat
                      seatNames={store.seats.selectedSeatNames}
                      type={ChecksSplitBySeatType.CreateChecks}
                      onDone={(seatNames) => void store.seats.createSeatChecks(seatNames)}
                    />
                  )
                } */}
            </>
          )}
        />
      )}
    />
  );
};

export { CheckPageSplit, sectionId };
